import React, { memo, useRef, useEffect, useState, useCallback, useMemo } from 'react';

import SectionList from '../SectionList';
import { useWeb5Context } from 'lib/Web5Context';

import SwitchCell from './editCells/SwitchCell';
import DatePickerCell from './editCells/DatePickerCell';
import SelectCell from './editCells/SelectCell';
import InputCell from './editCells/InputCell';
import CarouselCell from './editCells/CarouselCell';

import { createUseStyles } from '@Sage/hoi-poi-ui';
import styles from './styles';

const useStyles = createUseStyles(styles, 'SectionList');

const getInputPlaceholder = (crudType) => {
    switch (crudType) {
        case 'natural':
        case 'number':
        case 'integer':
            return 'placeholder_numeric';
        case 'currency':
        case 'decimal':
            return 'placeholder_amount';
        case 'percentage':
            return 'placeholder_percentage';
        case 'singleValueList':
            return 'label_selectone';
        case 'fuzzySearchSingle':
            return 'placeholder_search';
        case 'text':
        case 'textarea':
            return 'placeholder_text_field';
        case 'date':
            return 'placeholder_date_format';
        default:
            return '';
    }
};

const SectionListCrud = memo(
    ({
        getColumnDefs,
        defaultDateFormat,
        errors,
        rowData,
        onRef,
        shouldRefreshColumnDefs,
        idCurrency,
        idEnvironment,
        ...props
    }) => {
        const classes = useStyles();
        const errorsRef = useRef(errors);
        const tableRef = useRef(null);
        const isFirstLoad = useRef(true);
        const [columnDefs, setColumnsDefs] = useState([]);
        const { t } = useWeb5Context();
        const [hasErrors, setHasErrors] = useState(false);

        const crudComponents = useMemo(() => {
            return {
                SwitchCell,
                DatePickerCell,
                SelectCell,
                InputCell,
                CarouselCell,
            };
        }, []);

        const transformTableToCrud = useCallback(
            ({ columnDefs, crudSchema, inputProps, formatDate, getCurrencySymbolPosition }) => {
                const mappedCrudSchema = crudSchema.reduce((obj, current) => {
                    obj[current.id] = current;
                    return obj;
                }, {});

                const editCells = {
                    text: 'InputCell',
                    textarea: 'InputCell',
                    integer: 'InputCell',
                    decimal: 'InputCell',
                    number: 'InputCell',
                    percentage: 'InputCell',
                    currency: 'InputCell',
                    bool: 'SwitchCell',
                    singleValue: 'InputCell',
                    dateTime: 'DatePickerCell',
                    date: 'DatePickerCell',
                    singleValueList: 'SelectCell',
                    fuzzySearchSingle: 'SelectCell',
                    carousel: 'CarouselCell',
                };

                const crudColumnDef = columnDefs.map((current) => {
                    const crudField = mappedCrudSchema[current.colId] || null;
                    if (!crudField) return current;

                    const crudCellRenderer =
                        editCells[current.crudType] || editCells[crudField.type] || '';
                    if (!crudCellRenderer) return current;

                    // IMPORTANT
                    // >> crudField.type is related to extraFieldsSchema for extraFields
                    // >> current.crudType is related to the colDef of the standardFields
                    // so when is extrafield we look in crudField.type and when is standardField we look in current.crudType

                    const placeholder = getInputPlaceholder(crudField.type || current.crudType);

                    current.cellRenderer = crudCellRenderer;

                    current.cellRendererParams = {
                        ...current.cellRendererParams,
                        inputParams: {
                            ...current.cellRendererParams?.inputParams,
                            onChange: inputProps.onChange,
                            isMandatory: crudField.mandatory || false,
                            isReadOnly: crudField.readOnly || false,
                            dataLength: crudField.dataLength,
                            defaultValue: crudField.defaultValue,
                            placeholder: t(placeholder),
                            errors: errorsRef?.current?.[current.colId] || null,
                            preComponent:
                                crudField.inputAttrs?.preComponent ??
                                current.cellRendererParams?.inputParams?.preComponent ??
                                null,
                            postComponent:
                                crudField.inputAttrs?.postComponent ??
                                current.cellRendererParams?.inputParams?.postComponent ??
                                null,
                            idCurrency: idCurrency,
                            idEnvironment: idEnvironment,
                        },
                    };

                    if (crudField.mandatory) {
                        current.headerComponent = 'headerTextCell';
                        current.headerComponentParams = {
                            ...current.headerComponentParams,
                            isMandatory: true,
                        };
                    }

                    if (
                        crudField.type === 'natural' ||
                        crudField.type === 'integer' ||
                        crudField.type === 'number' ||
                        crudField.type === 'decimal' ||
                        crudField.type === 'currency' ||
                        crudField.type === 'percentage' ||
                        current.crudType === 'natural' ||
                        current.crudType === 'integer' ||
                        current.crudType === 'number' ||
                        current.crudType === 'decimal' ||
                        current.crudType === 'currency' ||
                        current.crudType === 'percentage'
                    ) {
                        current.cellRendererParams.inputParams.type =
                            crudField.type || current.crudType;
                    }

                    if (
                        (crudField.type === 'currency' || current.crudType === 'currency') &&
                        getCurrencySymbolPosition
                    ) {
                        const currencyPosition = getCurrencySymbolPosition();
                        current.cellRendererParams.currencyPosition = currencyPosition;
                    }

                    if ((crudField.type === 'date' || current.crudType === 'date') && formatDate) {
                        current.cellRendererParams.inputParams.formatDate = formatDate;
                    }

                    if (
                        crudField.type === 'singleValueList' ||
                        current.crudType === 'singleValueList'
                    ) {
                        current.cellRendererParams.inputParams = {
                            ...current.cellRendererParams.inputParams,
                            list: crudField?.inputAttrs?.list,
                            options: [],
                            isClearable: true,
                        };

                        if (crudField?.inputAttrs?.list && inputProps.loadOptions) {
                            current.cellRendererParams.inputParams = {
                                ...current.cellRendererParams.inputParams,
                                loadOptions: inputProps.loadOptions(crudField.inputAttrs),
                            };
                        }
                    }
                    if (
                        crudField.type === 'fuzzySearchSingle' ||
                        current.crudType === 'fuzzySearchSingle'
                    ) {
                        if (crudField?.inputAttrs?.list && inputProps.loadOptions) {
                            current.cellRendererParams.inputParams = {
                                ...current.cellRendererParams.inputParams,
                                loadOptions: inputProps.loadOptionsFuzzy(crudField.inputAttrs),
                                isFuzzy: true,
                                components: inputProps.selectComponents(crudField.inputAttrs),
                            };
                        }
                    }

                    return current;
                });

                return crudColumnDef;
            },
            [t, idCurrency, idEnvironment],
        );

        const updateErrors = useCallback(() => {
            if (!errorsRef.current) return;
            rowData.forEach((current, index) => {
                Object.keys(current).forEach((key) => {
                    if (current[key] || typeof current[key] === 'number') {
                        const directEntry = errorsRef?.current?.[key]?.rows?.[index];
                        if (directEntry && !directEntry.persistent) {
                            delete errorsRef?.current?.[key]?.rows?.[index];
                        } else if (!directEntry) {
                            const lowerEntry =
                                errorsRef?.current?.[key.toLowerCase()]?.rows?.[index];
                            if (lowerEntry && !lowerEntry.persistent) {
                                delete errorsRef?.current?.[key.toLowerCase()]?.rows?.[index];
                            }
                        }
                    }
                });
            });
        }, [rowData]);

        useEffect(() => {
            if (
                !isFirstLoad?.current &&
                errorsRef.current === errors &&
                !errors &&
                !shouldRefreshColumnDefs
            )
                return;
            if (!getColumnDefs) return;
            errorsRef.current = errors;
            updateErrors();
            getColumnDefs({ transformTableToCrud }).then((columnDefs) => {
                setColumnsDefs(columnDefs);
                if (tableRef.current && errorsRef.current) {
                    const colIds = Object.keys(errorsRef.current).map((key) => key);
                    tableRef.current.refreshColumnByColId(colIds);
                }
            });
            setHasErrors(errorsRef.current && Object.keys(errorsRef.current).length > 0);
            isFirstLoad.current = false;
        }, [
            getColumnDefs,
            transformTableToCrud,
            errors,
            rowData,
            updateErrors,
            shouldRefreshColumnDefs,
        ]);

        const handleOnRef = useCallback(
            (ref) => {
                tableRef.current = ref;
                onRef(ref);
            },
            [onRef],
        );

        const errorWrapperClass = useMemo(() => {
            let className = '';
            if (hasErrors) className = classes.errorsTable;
            return className;
        }, [classes, hasErrors]);

        return (
            <div className="w5-section-list-crud">
                <div className={errorWrapperClass}>
                    <SectionList
                        {...props}
                        columnDefs={columnDefs}
                        components={crudComponents}
                        rows={rowData}
                        onRef={handleOnRef}
                        rowHeight={65}
                        suppressColumnVirtualisation
                        suppressRowVirtualisation
                        rowBuffer={20}
                        forceRowUpdating
                    />
                </div>
            </div>
        );
    },
);

export default SectionListCrud;
